import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FetchArgs, PagingToken } from '@aya/controls/aya-table';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { appendQuery, arrayToQuery, combine } from 'src/app/shared/utilities/route';
import { DocumentAttribute } from '../shared/lib/compliance-shared/document-attributes-form/document-attributes-form.store';
import { MetadataConfiguration } from './models/acceptance-identifier.model';
import { CandidateVaccine } from './models/candidate-vaccine.model';

/**
 * @deprecated Do not use this service.  Use service(s) from "src/app/compliance/api" or "src/app/compliance/api/migration"
 */
@Injectable({ providedIn: 'root' })
export class MetadataService {
    private readonly coreApiMetadataPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) readonly settings: Settings
    ) {
        this.coreApiMetadataPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'metadata');
    }

    // ** MetadataController.GetMetadataFields
    getMetadataFields(documentTypeId: number, candidateDocumentId: number): Observable<any> {
        const url = `${this.coreApiMetadataPath}/fields?documentTypeId=${documentTypeId}&candidateDocumentId=${candidateDocumentId}`;
        return this.http.get<any>(url);
    }

    // ** MetadataController.GetFilteredMetadataFields
    getFilteredMetadataFields(documentTypeId: number, documentAttributes: DocumentAttribute[]): Observable<any> {
        const url = `${this.coreApiMetadataPath}/fields/filter`;
        return this.http.post<any>(url, {
            documentTypeId,
            values: documentAttributes
        });
    }

    // ** MetadataController.GetMissingMetadata
    getDocsWithMissingMetadata(args: FetchArgs): Observable<PagingToken<any[]>> {
        const { pageSize, skip } = args;
        let url = `${this.coreApiMetadataPath}/missing`;

        if (args.filter) {
            url += this.getQueryStringFetchArgs2(args.filter);
        }

        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sort.field=${args.sort.field}&sort.type=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PagingToken<any>>(path);
    }

    // ** MetadataController.SaveMetadata
    saveMetadata(
        candidateDocumentId: number,
        documentTypeId: number,
        contractId: number | null,
        metadata: DocumentAttribute[]
    ) {
        const url = `${this.coreApiMetadataPath}`;
        return this.http.put<any>(url, {
            candidateDocumentId,
            documentTypeId,
            contractId,
            metadata
        });
    }

    // ** MetadataController.GetCovidMetadata
    getCovidMetadata(candidateDocumentId: number, documentTypeId: number): Observable<CandidateVaccine> {
        const url = `${this.coreApiMetadataPath}/covid/${candidateDocumentId}/${documentTypeId}`;
        return this.http.get<CandidateVaccine>(url);
    }

    // ** MetadataController.GetMetadataConfiguration
    getMetadataConfiguration(documentTypeId: number): Observable<MetadataConfiguration> {
        const url = `${this.coreApiMetadataPath}/configuration/${documentTypeId}`;
        return this.http.get<MetadataConfiguration>(url);
    }

    private getQueryStringFetchArgs2(filter: CompositeFilterDescriptor) {
        let queryString = '';
        for (const prop in filter) {
            const val = filter[prop];
            if (Array.isArray(val)) {
                queryString = appendQuery(queryString, arrayToQuery(`filter.${prop}`, val));
            } else if (!isEmpty(val)) {
                queryString = appendQuery(queryString, `filter.${prop}=${val}`);
            }
        }

        return queryString;
    }
}
